import { shopifyFetch } from '../lib/shopify';
import { products as mockProducts, categories as mockCategories, articles as mockArticles } from '../data/mockData';

// ─── GraphQL Queries ──────────────────────────────────────────────────────────

const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $after: String) {
    products(first: $first, after: $after, sortKey: TITLE) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          handle
          description
          tags
          priceRange {
            minVariantPrice { amount currencyCode }
          }
          compareAtPriceRange {
            minVariantPrice { amount currencyCode }
          }
          images(first: 1) {
            edges { node { url altText } }
          }
          variants(first: 1) {
            edges {
              node {
                id
                availableForSale
              }
            }
          }
          collections(first: 1) {
            edges { node { title } }
          }
        }
      }
    }
  }
`;

const COLLECTIONS_QUERY = `
  query GetCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          image { url altText }
        }
      }
    }
  }
`;

const COLLECTION_PRODUCTS_QUERY = `
  query GetCollectionProducts($handle: String!, $first: Int!, $after: String) {
    collection(handle: $handle) {
      title
      description
      image { url altText }
      products(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            description
            tags
            priceRange {
              minVariantPrice { amount currencyCode }
            }
            compareAtPriceRange {
              minVariantPrice { amount currencyCode }
            }
            images(first: 1) {
              edges { node { url altText } }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  availableForSale
                }
              }
            }
            collections(first: 1) {
              edges { node { title } }
            }
          }
        }
      }
    }
  }
`;

const ARTICLES_QUERY = `
  query GetArticles($first: Int!) {
    blogs(first: 1) {
      edges {
        node {
          articles(first: $first) {
            edges {
              node {
                id
                title
                excerpt
                publishedAt
                image { url altText }
              }
            }
          }
        }
      }
    }
  }
`;

const FEATURED_PRODUCTS_QUERY = `
  query GetFeaturedProducts($first: Int!) {
    products(first: $first, query: "tag:bestselger") {
      edges {
        node {
          id
          title
          handle
          description
          tags
          priceRange {
            minVariantPrice { amount currencyCode }
          }
          compareAtPriceRange {
            minVariantPrice { amount currencyCode }
          }
          images(first: 1) {
            edges { node { url altText } }
          }
          variants(first: 1) {
            edges {
              node {
                id
                availableForSale
              }
            }
          }
          collections(first: 1) {
            edges { node { title } }
          }
        }
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation CartCreate($lines: [CartLineInput!]!) {
    cartCreate(input: { lines: $lines }) {
      cart {
        id
        checkoutUrl
      }
      userErrors { field message }
    }
  }
`;

// ─── Data Transformers ────────────────────────────────────────────────────────

function transformProduct(node) {
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const compareAt = parseFloat(node.compareAtPriceRange?.minVariantPrice?.amount || 0);
  const tags = node.tags || [];

  let badge = null;
  if (tags.some(t => /bestselger/i.test(t))) badge = 'Bestselger';
  else if (tags.some(t => /tilbud|sale|rabatt/i.test(t))) badge = 'Tilbud';
  else if (tags.some(t => /ny|new|nyhet/i.test(t))) badge = 'Nyhet';
  else if (tags.some(t => /anbefalt|utvalgt/i.test(t))) badge = 'Anbefalt';

  return {
    id: node.id,
    handle: node.handle,
    name: node.title,
    scientific: '',
    description: node.description || '',
    price: Math.round(price),
    originalPrice: compareAt > price ? Math.round(compareAt) : null,
    image: node.images.edges[0]?.node.url || null,
    badge,
    category: node.collections.edges[0]?.node.title || '',
    variantId: node.variants.edges[0]?.node.id || null,
    available: node.variants.edges[0]?.node.availableForSale ?? true,
  };
}

function transformCollection(node) {
  return {
    id: node.id,
    name: node.title,
    handle: node.handle,
    image: node.image?.url || null,
    count: node.description || '',
  };
}

function transformArticle(node) {
  return {
    id: node.id,
    title: node.title,
    excerpt: node.excerpt || '',
    image: node.image?.url || null,
    date: new Date(node.publishedAt).toLocaleDateString('nb-NO', {
      month: 'long',
      year: 'numeric',
    }),
  };
}

// ─── Pagination Helper ────────────────────────────────────────────────────────

async function fetchAllProducts() {
  let allProducts = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const variables = { first: 250, ...(cursor && { after: cursor }) };
    const data = await shopifyFetch({ query: PRODUCTS_QUERY, variables });
    const { edges, pageInfo } = data.products;
    allProducts = [...allProducts, ...edges.map(({ node }) => transformProduct(node))];
    hasNextPage = pageInfo.hasNextPage;
    cursor = pageInfo.endCursor;
  }

  return allProducts;
}

async function fetchAllCollectionProducts(handle) {
  let allProducts = [];
  let cursor = null;
  let hasNextPage = true;
  let collectionInfo = null;

  while (hasNextPage) {
    const variables = { handle, first: 250, ...(cursor && { after: cursor }) };
    const data = await shopifyFetch({ query: COLLECTION_PRODUCTS_QUERY, variables });
    if (!data.collection) return { products: [], info: null };
    collectionInfo = data.collection;
    const { edges, pageInfo } = data.collection.products;
    allProducts = [...allProducts, ...edges.map(({ node }) => transformProduct(node))];
    hasNextPage = pageInfo.hasNextPage;
    cursor = pageInfo.endCursor;
  }

  return { products: allProducts, info: collectionInfo };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const storefrontService = {

  async getProducts() {
    try {
      const shopifyProducts = await fetchAllProducts();
      return shopifyProducts.length > 0 ? shopifyProducts : mockProducts;
    } catch (err) {
      console.warn('Shopify getProducts feilet, bruker mock-data:', err.message);
      return mockProducts;
    }
  },

  async getProductByHandle(handle) {
    try {
      const all = await fetchAllProducts();
      return all.find(p => p.handle === handle) || null;
    } catch (err) {
      console.warn('Shopify getProductByHandle feilet:', err.message);
      return mockProducts.find(p => String(p.id) === String(handle)) || null;
    }
  },

  async getFeaturedProducts() {
    try {
      const data = await shopifyFetch({ query: FEATURED_PRODUCTS_QUERY, variables: { first: 12 } });
      const products = data.products.edges.map(({ node }) => transformProduct(node));
      return products;
    } catch (err) {
      console.warn('Shopify getFeaturedProducts feilet:', err.message);
      return [];
    }
  },

  async getCollections() {
    try {
      const data = await shopifyFetch({ query: COLLECTIONS_QUERY, variables: { first: 50 } });
      const collections = data.collections.edges
        .map(({ node }) => transformCollection(node))
        .filter(c => c.handle !== 'frontpage');
      return collections.length > 0 ? collections : mockCategories;
    } catch (err) {
      console.warn('Shopify getCollections feilet, bruker mock-data:', err.message);
      return mockCategories;
    }
  },

  async getProductsByCollection(handle) {
    try {
      const { products } = await fetchAllCollectionProducts(handle);
      return products.length > 0 ? products : mockProducts;
    } catch (err) {
      console.warn('Shopify getProductsByCollection feilet:', err.message);
      return mockProducts;
    }
  },

  async getArticles() {
    try {
      const data = await shopifyFetch({ query: ARTICLES_QUERY, variables: { first: 10 } });
      const blog = data.blogs.edges[0]?.node;
      if (!blog) return mockArticles;
      const articles = blog.articles.edges.map(({ node }) => transformArticle(node));
      return articles.length > 0 ? articles : mockArticles;
    } catch (err) {
      console.warn('Shopify getArticles feilet, bruker mock-data:', err.message);
      return mockArticles;
    }
  },

  async createCheckoutUrl(cartItems) {
    const lines = cartItems
      .filter(item => item.variantId)
      .map(item => ({ merchandiseId: item.variantId, quantity: item.quantity }));

    if (lines.length === 0) {
      throw new Error('Ingen gyldige produkter i handlekurven');
    }

    const data = await shopifyFetch({ query: CART_CREATE_MUTATION, variables: { lines } });

    if (data.cartCreate.userErrors?.length) {
      throw new Error(data.cartCreate.userErrors[0].message);
    }

    return data.cartCreate.cart.checkoutUrl;
  },
};
